import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { DelinquentReport, DelinquentParams, ExportFormat } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

const DEFAULT_PARAMS: DelinquentParams = {
  page: 1,
  limit: 20,
  minDaysOverdue: 1,
  sortBy: 'daysOverdue',
  sortOrder: 'DESC',
};

export const useDelinquentReport = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const [report, setReport] = useState<DelinquentReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<DelinquentParams>(DEFAULT_PARAMS);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await reportRepo.getDelinquentReport(params);
      setReport(data);
    } catch (e) {
      console.error('Error loading delinquent report:', e);
      toast.error('Error al cargar el reporte de morosos');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(async (format: ExportFormat) => {
    setExporting(true);
    try {
      const blob = await reportRepo.exportDelinquentReport(format, params);
      const filename = generateReportFilename('morosos', format);
      downloadBlob(blob, filename);
      toast.success('Reporte exportado correctamente');
    } catch (e) {
      console.error('Error exporting delinquent report:', e);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  }, [reportRepo, params, toast]);

  const updateParams = useCallback((newParams: Partial<DelinquentParams>) => {
    setParams(prev => ({ ...prev, ...newParams }));
  }, []);

  const resetParams = useCallback(() => {
    setParams(DEFAULT_PARAMS);
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    loading,
    exporting,
    params,
    setParams,
    updateParams,
    resetParams,
    refetch: fetchReport,
    exportReport,
  };
};
